const NODE_RADIUS = 30;
const MIN_DIST = NODE_RADIUS * 2.4;

export function rootNodeId(entityType, entityId) {
  return `${entityType}:${entityId}`;
}

export function layoutGraph(nodes, edges, rootId, options = {}) {
  const width = options.width || 920;
  const height = options.height || 560;
  const iterations = options.iterations || 120;

  if (!nodes.length) return [];

  const simNodes = nodes.map((node, index) => {
    const angle = (index / Math.max(nodes.length, 1)) * Math.PI * 2;
    const ring = Math.min(width, height) * 0.28;
    return {
      ...node,
      x: width / 2 + Math.cos(angle) * ring,
      y: height / 2 + Math.sin(angle) * ring,
      vx: 0,
      vy: 0,
      isRoot: node.id === rootId,
    };
  });

  const nodeMap = Object.fromEntries(simNodes.map((node) => [node.id, node]));
  const links = (edges || [])
    .map((edge) => ({ source: nodeMap[edge.source], target: nodeMap[edge.target] }))
    .filter((link) => link.source && link.target);

  const root = nodeMap[rootId];

  for (let tick = 0; tick < iterations; tick += 1) {
    const cooling = 1 - tick / iterations;

    for (let i = 0; i < simNodes.length; i += 1) {
      for (let j = i + 1; j < simNodes.length; j += 1) {
        const a = simNodes[i];
        const b = simNodes[j];
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let dist = Math.hypot(dx, dy) || 0.01;
        const repulsion = ((MIN_DIST * MIN_DIST) / dist) * (0.8 + cooling * 0.4);
        dx = (dx / dist) * repulsion;
        dy = (dy / dist) * repulsion;
        if (!a.isRoot) {
          a.vx -= dx;
          a.vy -= dy;
        }
        if (!b.isRoot) {
          b.vx += dx;
          b.vy += dy;
        }
      }
    }

    links.forEach(({ source, target }) => {
      let dx = target.x - source.x;
      let dy = target.y - source.y;
      let dist = Math.hypot(dx, dy) || 0.01;
      const spring = (dist - MIN_DIST * 1.35) * 0.08;
      dx = (dx / dist) * spring;
      dy = (dy / dist) * spring;
      if (!source.isRoot) {
        source.vx += dx;
        source.vy += dy;
      }
      if (!target.isRoot) {
        target.vx -= dx;
        target.vy -= dy;
      }
    });

    simNodes.forEach((node) => {
      if (node.isRoot && root) {
        node.x = width / 2;
        node.y = height / 2;
        node.vx = 0;
        node.vy = 0;
        return;
      }
      node.vx *= 0.82;
      node.vy *= 0.82;
      node.x += node.vx * cooling;
      node.y += node.vy * cooling;
      node.x = Math.max(NODE_RADIUS + 12, Math.min(width - NODE_RADIUS - 12, node.x));
      node.y = Math.max(NODE_RADIUS + 28, Math.min(height - NODE_RADIUS - 12, node.y));
    });
  }

  return simNodes;
}

export function neighborSets(nodeId, edges = []) {
  const nodeIds = new Set(nodeId ? [nodeId] : []);
  const edgeIds = new Set();

  if (!nodeId) {
    return { nodeIds, edgeIds };
  }

  edges.forEach((edge) => {
    if (edge.source === nodeId || edge.target === nodeId) {
      edgeIds.add(edge.id);
      nodeIds.add(edge.source);
      nodeIds.add(edge.target);
    }
  });

  return { nodeIds, edgeIds };
}

export function truncateLabel(value, max = 18) {
  const text = String(value || '').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

/** Map screen coordinates to graph space (inside the pan/zoom group). */
export function clientToGraphCoords(svgElement, clientX, clientY, graphGroup) {
  if (!svgElement || !graphGroup) return { x: 0, y: 0 };

  const point = svgElement.createSVGPoint();
  point.x = clientX;
  point.y = clientY;
  const matrix = graphGroup.getScreenCTM?.();
  if (!matrix) return { x: 0, y: 0 };

  const graphPoint = point.matrixTransform(matrix.inverse());
  return { x: graphPoint.x, y: graphPoint.y };
}

export function mergeLayoutWithOverrides(layout, overrides = {}) {
  return layout.map((node) => {
    const override = overrides[node.id];
    if (!override) return node;
    return { ...node, x: override.x, y: override.y };
  });
}
